import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, Clock, TrendingUp } from 'lucide-react';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const FidelizacaoAlerts = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [candidatos, setCandidatos] = useState([]);
  const [stats, setStats] = useState({
    totalFidelizacoes: 0,
    ativas: 0,
    vencendoEm30Dias: 0,
    receitaProtegida: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Busca candidatos à refidelização (vencendo em 30 dias)
      const candidatosRes = await api.get('/contratos/refidelizar/candidatos?dias=30');
      setCandidatos(candidatosRes.data);
      
      // Busca estatísticas de fidelização
      try {
        const statsRes = await api.get('/contratos/fidelizacoes/relatorio?ativas=true');
        setStats({
          totalFidelizacoes: statsRes.data.totalFidelizacoes || 0,
          ativas: statsRes.data.ativas || 0,
          vencendoEm30Dias: candidatosRes.data.length,
          receitaProtegida: statsRes.data.receitaProtegida || 0
        });
      } catch (error) {
        console.error('Erro ao buscar estatísticas:', error);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados de fidelização');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex justify-between items-start mb-2">
            <Shield className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-3xl font-bold">{stats.totalFidelizacoes}</p>
          <p className="text-sm opacity-90">Total de Fidelizações</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex justify-between items-start mb-2">
            <TrendingUp className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-3xl font-bold">{stats.ativas}</p>
          <p className="text-sm opacity-90">Fidelizações Ativas</p>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white">
          <div className="flex justify-between items-start mb-2">
            <Clock className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-3xl font-bold">{stats.vencendoEm30Dias}</p>
          <p className="text-sm opacity-90">Vencendo em 30 Dias</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex justify-between items-start mb-2">
            <DollarSign className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-3xl font-bold">
            R$ {stats.receitaProtegida.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </p>
          <p className="text-sm opacity-90">Receita Protegida</p>
        </div>
      </div>

      {/* Alertas de Fidelizações Vencendo */}
      {candidatos.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-yellow-600" />
            <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-200">
              Contratos Elegíveis para Refidelização
            </h3>
          </div>

          <div className="space-y-3">
            {candidatos.slice(0, 5).map((candidato) => (
              <div
                key={candidato.contratoId}
                className="bg-white dark:bg-dark-card rounded-lg p-4 flex justify-between items-center hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/contratos/${candidato.contratoId}`)}
              >
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {candidato.clienteNome}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Fidelização termina em {candidato.diasRestantes} dias
                  </p>
                  <p className="text-sm text-gray-500">
                    Vence em: {format(new Date(candidato.dataFimFidelizacao), 'dd/MM/yyyy')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    R$ {candidato.valorMensalidade}
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/contratos/${candidato.contratoId}`);
                    }}
                    className="mt-2 px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                  >
                    Refidelizar
                  </button>
                </div>
              </div>
            ))}
          </div>

          {candidatos.length > 5 && (
            <button
              onClick={() => navigate('/relatorios/fidelizacoes')}
              className="mt-4 w-full py-2 border border-yellow-300 dark:border-yellow-700 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900/30 text-sm font-medium"
            >
              Ver todos os {candidatos.length} candidatos
            </button>
          )}
        </div>
      )}

      {candidatos.length === 0 && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 text-center">
          <Shield className="w-12 h-12 text-green-600 mx-auto mb-3" />
          <p className="text-green-800 dark:text-green-200 font-medium">
            Nenhuma fidelização próxima do vencimento
          </p>
          <p className="text-sm text-green-600 dark:text-green-400 mt-1">
            Todas as fidelizações estão em dia
          </p>
        </div>
      )}
    </div>
  );
};

// Adicionar import do DollarSign
import { DollarSign } from 'lucide-react';

export default FidelizacaoAlerts;
